import cn from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { UPDATE_ITEM_QUANTITY } from 'constants/checkoutButtons';
import {
  DELIVERY_INFORMATION_MISSING,
  DELIVERY_OPTION,
  FULLFILMENT_NETWORK_MISSING,
  INVALID_SELECTED_DELIVERY_OPTION_FOR_LINE_ITEM,
  ITEM_QUANTITY_UNAVAILABLE,
  JURISDICTION,
  MISSING_DIGITAL_DELIVERY_INFORMATION,
  OFFER_LISTING_AND_OFFER_SKU_DIFFER,
  OFFER_LISTING_NOT_AVAILABLE_CONSTRAINT_VIOLATION,
  QUANTITY_LIMITS,
  SHIPPING_ENGINE_FILTER_BASED,
  SHIPPING_ENGINE_PROVIDER_BASED,
  SHIPPING_ENGINE_REMOVER_BASED,
  SHIPPING_ENGINE_SHIPPING_OFFERING_NOT_SET
} from 'constants/constraintViolations';

import marketplace from 'cfg/marketplace.json';
import { Select } from 'components/Mdl';
import ContentBox from 'components/checkout/caap/ContentBox';
import { makeOptions } from 'components/cart/CartQuantityDropdown';
import { buildNoShipMessage, getFieldFocusEvents } from 'helpers/CheckoutUtils';
import { toUSD } from 'helpers/NumberFormats';
import css from 'styles/components/checkout/caap/spc/reviewItems.scss';
import giftOptionsCss from 'styles/components/checkout/caap/spc/reviewitems/giftOptions.scss';
const { checkout: { allowGiftOptions } } = marketplace;

const fieldFocusEvents = getFieldFocusEvents(css.hasFormElementWithFocus);

const RetailItems = (params, { testId }) => {
  const {
    asinErrors,
    deliveryGroup,
    groupHeader,
    isFetchingCheckoutData,
    onChangeQuantity,
    onViewGiftOptionsClick,
    productsByLineItem,
    shipOptionsSection,
    hasShipOptions,
    excludeShipOptions,
    showTopDivider,
    isOrderReadyToSubmit,
    isInvalidGiftOption,
    eligibleForGiftWrap
  } = params;

  const { lineItemIds } = deliveryGroup;
  const noShipOptionMessage = buildNoShipMessage(lineItemIds.length > 1);

  const wrapperStyles = {
    [css.formInputWrapper]: true
  };

  const quantityWrapperStyles = cn(
    {
      ...wrapperStyles,
      [css.quantityContainer]: true,
      [css.solo]: true
    }
  );

  return (
    <>
      <div className={css.showOnlySmallScreen}>
        <h2 data-test-id={hasShipOptions ? testId('retailShippableShipmentDetails') : testId('retailNonShippableShipmentDetails') }>
          { groupHeader }
        </h2>
      </div>
      {
        !excludeShipOptions && (hasShipOptions || isOrderReadyToSubmit) && <ContentBox isFirst={true} isNonRadio={true}>
          {
            hasShipOptions
              ? shipOptionsSection
              : <div className={css.errorContainer}>
                {noShipOptionMessage}
              </div>
          }
        </ContentBox>
      }

      {
        lineItemIds.slice().map((unmappedLineItemId, index) => {
          const mappedItem = productsByLineItem[unmappedLineItemId];
          const {
            asin,
            color,
            displaySize,
            image: { url: imageUrl, alt: imageAlt },
            itemLengthDescription,
            lineItemId,
            onHand,
            price,
            productName,
            quantity,
            shoeWidth,
            giftMessagable,
            giftMessage,
            hasGiftReceipt,
            giftWrapId
          } = mappedItem;

          const isLast = index === lineItemIds.length - 1;
          const isFirst = index === 0 && excludeShipOptions;
          const updateQuantityButtonId = `${UPDATE_ITEM_QUANTITY}-${lineItemId}`;
          const rowStyles = cn(css.itemRow, { [css.last]: isLast, [css.first]: isFirst });

          return (
            <div className={rowStyles} key={`${lineItemId}-${index}`} data-test-id={hasShipOptions ? testId('retailShippableItemContainer') : testId('retailNonShippableItemContainer')}>
              { excludeShipOptions && showTopDivider && <hr className={css.divider} /> }
              { !showTopDivider && index !== 0 && <hr className={css.divider} /> }
              <div className={css.imgBox}>
                <Link
                  to={`/p/asin/${asin}`}
                  data-te="TE_CHECKOUT_REV_GO_TO_PDP"
                  data-ted={asin}
                  data-test-id={testId('productImage')}>
                  <img width="80" src={imageUrl} alt={imageAlt} />
                </Link>
              </div>

              <div className={css.productDetailsBox}>
                <div className={css.productName}>
                  <Link
                    to={`/p/asin/${asin}`}
                    data-te="TE_CHECKOUT_REV_GO_TO_PDP"
                    data-ted={asin}
                    data-test-id={testId('productName')}>
                    {productName}
                  </Link>
                </div>
                <div data-test-id={testId('itemColor')}>Color: {color}</div>
                {displaySize ? <div data-test-id={testId('itemSize')}>Size: {displaySize}</div> : ''}
                {shoeWidth ? <div data-test-id={testId('itemWidth')}>Width: {shoeWidth}</div> : ''}
                {itemLengthDescription ? <div data-test-id={testId('itemLengthDescription')}>Inseam: {itemLengthDescription}</div> : ''}
                <div className={`${css.itemPrice} ${css.showOnlySmallScreen}`} data-test-id={testId('itemPrice')}>{toUSD(price)}</div>
                { makeGiftOptionsBtn({ eligibleForGiftWrap, lineItemId, giftMessagable, giftMessage, hasGiftReceipt, giftWrapId, onViewGiftOptionsClick }) }
              </div>

              <div className={css.desktopPriceBox}>
                <div className={css.itemPrice} data-test-id={testId('itemPrice')}>{toUSD(price)}</div>
              </div>

              {
                mustBeRemovedFromPurchase({ asinErrors, asin })
                  ? <div className={`${css.quantityBox} ${css.quantityWrapper} ${css.form}`}>
                    <button
                      className={css.checkoutSecondaryButton}
                      disabled={isFetchingCheckoutData}
                      id={updateQuantityButtonId}
                      onClick={e => onChangeQuantity(e, lineItemId)}
                      type="button"
                      value="0">
                      remove
                    </button>
                  </div>
                  : <div className={`${css.quantityBox} ${css.quantityWrapper} ${css.form}`}>
                    <label className={css.showOnlySmallScreen} htmlFor={updateQuantityButtonId}>Quantity:</label>
                    <div { ...fieldFocusEvents } className={quantityWrapperStyles}>
                      <Select
                        value={quantity}
                        onChange={e => onChangeQuantity(e, lineItemId)}
                        id={updateQuantityButtonId}
                        disabled={isFetchingCheckoutData}
                        data-test-id={testId('quantitySelector')}>
                        <option value="0">Remove</option>
                        {makeOptions(onHand, quantity)}
                      </Select>
                    </div>
                  </div>
              }

              <div className={css.errorBox}>
                { makeAsinErrors({ asinErrors, asin }) }
                {
                  isInvalidGiftOption && hasGiftReceipt && <div className={css.errorContainer}>
                  Sorry, gift options cannot be added to this item. We are working to fix this. Please remove the gift options that you have added.
                  </div>
                }
              </div>
            </div>
          );
        })
      }
    </>
  );
};

export function makeGiftOptionsBtn(params) {
  const {
    eligibleForGiftWrap,
    lineItemId,
    giftMessagable,
    giftMessage,
    hasGiftReceipt,
    giftWrapId,
    onViewGiftOptionsClick
  } = params;

  if (!allowGiftOptions || !eligibleForGiftWrap) {
    return null;
  }

  let message = '';

  switch (true) {
    case hasGiftReceipt && !!giftWrapId:
      message = 'Gift Receipt and Gift Bag/Box selected';
      break;
    case hasGiftReceipt && !!giftMessage:
      message = 'Gift Receipt and Gift Message selected';
      break;
    case hasGiftReceipt:
      message = 'Gift Receipt selected';
      break;
  }

  if (!giftMessagable) {
    return <div>
      <span className={giftOptionsCss.gift}></span>Gift options not available
    </div>;
  }

  if (message.length) {
    return (
      <>
        <div className={css.giftMessageText}>
          <span className={giftOptionsCss.gift}></span>Gift options: {message}
        </div>
        <div>
          <button type="button" className={giftOptionsCss.changeButton} onClick={e => onViewGiftOptionsClick(e, { lineItemId, giftMessage, giftWrapId })}>
            Change Gift Options
          </button>
        </div>
      </>
    );
  }

  return (
    <button type="button" className={giftOptionsCss.giftOptionsButton} onClick={e => onViewGiftOptionsClick(e, { lineItemId, giftMessage, giftWrapId })}>
      <span className={giftOptionsCss.gift}/>
      Make this a gift
    </button>
  );
}

export function mustBeRemovedFromPurchase({ asinErrors, asin }) {
  if (!asinErrors?.hasOwnProperty(asin)) {
    return false;
  }

  const asinErrorList = [
    DELIVERY_OPTION,
    DELIVERY_INFORMATION_MISSING,
    SHIPPING_ENGINE_FILTER_BASED
  ];

  const result = asinErrors[asin].filter(info => asinErrorList.some(name => name === info.name));

  return !!result.length;
}

export function makeAsinErrors({ asinErrors, asin }) {
  if (!asinErrors || !asinErrors[asin]) {
    return null;
  }

  const asinErrorList = [
    DELIVERY_OPTION,
    DELIVERY_INFORMATION_MISSING,
    FULLFILMENT_NETWORK_MISSING,
    ITEM_QUANTITY_UNAVAILABLE,
    JURISDICTION,
    MISSING_DIGITAL_DELIVERY_INFORMATION,
    OFFER_LISTING_AND_OFFER_SKU_DIFFER,
    OFFER_LISTING_NOT_AVAILABLE_CONSTRAINT_VIOLATION,
    QUANTITY_LIMITS,
    SHIPPING_ENGINE_FILTER_BASED,
    SHIPPING_ENGINE_REMOVER_BASED,
    SHIPPING_ENGINE_PROVIDER_BASED,
    SHIPPING_ENGINE_SHIPPING_OFFERING_NOT_SET,
    INVALID_SELECTED_DELIVERY_OPTION_FOR_LINE_ITEM
  ];

  const result = asinErrors[asin].filter(info => asinErrorList.some(name => name === info.name)).map(cv => {
    switch (cv.name) {
      case ITEM_QUANTITY_UNAVAILABLE: {
        const message = `${cv.message} Please remove one or more of this item. There ${cv.quantityAvailable > 1 ? 'are' : 'is'} ${cv.quantityAvailable} available in stock.`;
        return <p key={cv.name}>{message}</p>;
      }
      case INVALID_SELECTED_DELIVERY_OPTION_FOR_LINE_ITEM: {
        return <p key={cv.name}>
          <strong>Please select a different shipping speed.</strong>  Unfortunately, this item cannot be shipped with the chosen shipment speed.
        </p>;
      }
      case SHIPPING_ENGINE_FILTER_BASED:
      case DELIVERY_OPTION:
      case DELIVERY_INFORMATION_MISSING: {
        return <p key={cv.name}>
          <strong>Please remove this item.</strong> Unfortunately, this item has no valid shipping option. We are working on fixing this issue on our end.
        </p>;
      }
      default: {
        return <p key={cv.name}>{cv.message}</p>;
      }
    }
  });

  return result.length ? (
    <div className={css.errorContainer}>
      {result}
    </div>
  ) : '';
}

RetailItems.contextTypes = {
  testId: PropTypes.func
};

export default RetailItems;
